// src/utils/tokenUtils.js

import { jwtDecode } from "jwt-decode";

/**
 * Generate a JWT token (for frontend demonstration only)
 * Note: In a real app, tokens should be generated on the backend
 * @param {string} userId - The user ID to include in the token
 * @returns {string} JWT token
 */
export const generateToken = (userId) => {
  // For demo purposes only - in real applications, tokens are generated server-side
  // This is just to demonstrate the format and validation process
  const header = {
    alg: "HS256",
    typ: "JWT",
  };

  const currentTime = Math.floor(Date.now() / 1000);
  const expiresIn = 60 * 60 * 24 * 7; // 7 days in seconds

  const payload = {
    id: userId,
    iat: currentTime,
    exp: currentTime + expiresIn,
  };

  // Note: In a real implementation, this would use a library like jsonwebtoken
  // This is just a demonstration of the structure
  const tokenParts = [
    btoa(JSON.stringify(header)),
    btoa(JSON.stringify(payload)),
    "signature", // In a real app, this would be an HMAC-SHA256 signature
  ];

  return tokenParts.join(".");
};

/**
 * Check if a token is valid
 * @param {string} token - JWT token to validate
 * @returns {boolean} Whether the token is valid
 */
export const isTokenValid = (token) => {
  if (!token) return false;

  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000;

    return decodedToken.exp > currentTime;
  } catch (error) {
    return false;
  }
};

/**
 * Get user info from token
 * @param {string} token - JWT token
 * @returns {object|null} User info or null if token is invalid
 */
export const getUserInfoFromToken = (token) => {
  if (!token) return null;

  try {
    const decodedToken = jwtDecode(token);
    return {
      userId: decodedToken.id,
      // Add other user properties as needed
    };
  } catch (error) {
    return null;
  }
};
